/**
 * SusPage — hub da Auditoria do SUS (F9).
 *
 * Réplica dos sus/index.html + sus/novo_parecer.html: a lista de pareceres
 * e o formulário de criação. O tipo "Plano de Aplicação de Recurso" pede a
 * fonte (FNS/FES), que entra no rótulo final do documento.
 */

import {
  faCheckCircle,
  faExclamationTriangle,
  faEye,
  faFileMedical,
  faHospital,
  faPaperclip,
  faPills,
  faSearch,
  faSpinner,
  faStethoscope,
  faTimes,
  faTrashAlt,
  faWandMagicSparkles,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import {
  buscarNoCatalogoSUS,
  configuracaoSUS,
  contextosAnteriores,
  criarParecer,
  excluirParecer,
  type ItemCatalogoSUS,
  listarPareceres,
  melhorarContexto,
} from '../../api/sus'
import { useToast } from '../../components/Toast'

const TIPO_COM_FONTE = 'Plano de Aplicação de Recurso'

/**
 * Busca na RENAME (medicamentos) ou na Tabela SUS/SIGTAP (procedimentos).
 * O que é escolhido vira texto e viaja para o agente, que cita a norma no
 * parecer — é assim que o documento sai com o código SIGTAP certo.
 */
function SeletorCatalogo({
  tipo,
  titulo,
  icone,
  escolhidos,
  aoEscolher,
}: {
  tipo: 'medicamento' | 'procedimento'
  titulo: string
  icone: typeof faPills
  escolhidos: string[]
  aoEscolher: (itens: string[]) => void
}) {
  const [termo, setTermo] = useState('')
  const [busca, setBusca] = useState('')

  const { data, isFetching } = useQuery({
    queryKey: ['sus-catalogo', tipo, busca],
    queryFn: () => buscarNoCatalogoSUS(tipo, busca),
    enabled: busca.length >= 2,
  })

  const rotulo = (i: ItemCatalogoSUS) =>
    [i.codigo, i.titulo].filter(Boolean).join(' - ')

  return (
    <div className="rounded-xl border border-slate-200 p-4 dark:border-slate-700">
      <p className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300">
        <FontAwesomeIcon icon={icone} className="text-teal-600" />
        {titulo}
      </p>
      <div className="flex gap-2">
        <input
          type="search"
          aria-label={`Buscar ${titulo.toLowerCase()}`}
          value={termo}
          onChange={(e) => setTermo(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              setBusca(termo.trim())
            }
          }}
          placeholder="Digite ao menos 2 caracteres e tecle Enter"
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-600 dark:bg-dark-bg dark:text-slate-100"
        />
        <button
          type="button"
          onClick={() => setBusca(termo.trim())}
          className="rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-700 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200"
        >
          <FontAwesomeIcon icon={isFetching ? faSpinner : faSearch} spin={isFetching} />
        </button>
      </div>

      {busca.length >= 2 && (
        <ul className="mt-2 max-h-52 space-y-1 overflow-y-auto">
          {(data?.itens ?? []).length === 0 && !isFetching ? (
            <li className="p-2 text-sm text-slate-500">Nada encontrado.</li>
          ) : (
            (data?.itens ?? []).map((i, indice) => (
              <li key={`${i.codigo}-${indice}`}>
                <button
                  type="button"
                  onClick={() => {
                    const texto = rotulo(i)
                    if (!escolhidos.includes(texto))
                      aoEscolher([...escolhidos, texto])
                  }}
                  className="w-full rounded-lg p-2 text-left text-sm hover:bg-teal-50 dark:hover:bg-teal-900/20"
                >
                  <span className="block font-medium text-slate-800 dark:text-slate-100">
                    {rotulo(i)}
                  </span>
                  {i.detalhe && (
                    <span className="block text-xs text-slate-500">
                      {i.detalhe}
                      {i.origem && ` · ${i.origem}`}
                    </span>
                  )}
                </button>
              </li>
            ))
          )}
        </ul>
      )}

      {escolhidos.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {escolhidos.map((e) => (
            <span
              key={e}
              className="inline-flex items-center gap-2 rounded-full bg-teal-100 px-3 py-1 text-xs text-teal-800 dark:bg-teal-900/30 dark:text-teal-300"
            >
              {e}
              <button
                type="button"
                aria-label={`Remover ${e}`}
                onClick={() => aoEscolher(escolhidos.filter((x) => x !== e))}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

export function SusPage() {
  const navegar = useNavigate()
  const clienteQuery = useQueryClient()
  const { notificar } = useToast()
  const [campos, setCampos] = useState<Record<string, string>>({})
  const [medicamentos, setMedicamentos] = useState<string[]>([])
  const [procedimentos, setProcedimentos] = useState<string[]>([])
  const [anexos, setAnexos] = useState<File[]>([])
  const [verContextos, setVerContextos] = useState(false)

  const { data: configuracao } = useQuery({
    queryKey: ['sus-configuracao'],
    queryFn: configuracaoSUS,
    staleTime: Infinity,
  })
  const { data, isLoading } = useQuery({
    queryKey: ['sus-pareceres'],
    queryFn: listarPareceres,
  })

  const { data: contextos } = useQuery({
    queryKey: ['sus-contextos'],
    queryFn: () => contextosAnteriores(),
    enabled: verContextos,
  })

  const melhorar = useMutation({
    mutationFn: () => melhorarContexto(campos.contexto ?? '', campos.tipo ?? ''),
    onSuccess: (r) => {
      definir('contexto', r.contexto)
      notificar('success', 'Contexto refinado pela IA.')
    },
    onError: (e: Error) => notificar('error', e.message),
  })

  const criar = useMutation({
    mutationFn: () =>
      criarParecer(
        {
          tipo: campos.tipo ?? '',
          assunto: campos.assunto ?? '',
          destinatario: campos.destinatario ?? '',
          jurisdicao: campos.jurisdicao ?? '',
          contexto: campos.contexto ?? '',
          fonte_recurso: campos.fonte_recurso ?? '',
          medicamentos,
          procedimentos,
        },
        anexos,
      ),
    onSuccess: (r) => {
      void clienteQuery.invalidateQueries({ queryKey: ['sus-pareceres'] })
      navegar(`/tasks/parecer/${r.id}`)
    },
    onError: (e: Error) => notificar('error', e.message),
  })

  const excluir = useMutation({
    mutationFn: (id: number) => excluirParecer(id),
    onSuccess: () => {
      void clienteQuery.invalidateQueries({ queryKey: ['sus-pareceres'] })
      notificar('success', 'Parecer excluído.')
    },
    onError: (e: Error) => notificar('error', e.message),
  })

  const definir = (campo: string, valor: string) =>
    setCampos((atuais) => ({ ...atuais, [campo]: valor }))
  const entrada =
    'w-full rounded-lg border border-slate-300 px-4 py-2 text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-slate-600 dark:bg-dark-bg dark:text-slate-100'
  const pareceres = data?.pareceres ?? []

  return (
    <div className="mx-auto max-w-5xl">
      <header className="mb-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <div className="bg-gradient-to-r from-teal-600 via-cyan-600 to-teal-500 p-6 text-white">
          <h1 className="flex items-center text-2xl font-bold">
            <FontAwesomeIcon icon={faHospital} className="mr-3" />
            Auditoria do SUS
          </h1>
          <p className="mt-1 text-teal-100">
            Pareceres técnicos, notas e planos de aplicação de recurso, com
            fundamentação normativa do SUS.
          </p>
        </div>

        <form
          className="space-y-4 p-6"
          onSubmit={(e) => {
            e.preventDefault()
            if (!campos.tipo || !campos.assunto?.trim()) {
              notificar('error', 'Escolha o tipo e informe o assunto.')
              return
            }
            criar.mutate()
          }}
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="tipo" className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Tipo de documento <span className="text-red-500">*</span>
              </label>
              <select
                id="tipo"
                name="tipo"
                value={campos.tipo ?? ''}
                onChange={(e) => definir('tipo', e.target.value)}
                className={entrada}
              >
                <option value="">-- Selecione --</option>
                {(configuracao?.tipos ?? []).map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>

            {campos.tipo === TIPO_COM_FONTE && (
              <div>
                <label htmlFor="fonte_recurso" className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Fonte do recurso
                </label>
                <select
                  id="fonte_recurso"
                  name="fonte_recurso"
                  value={campos.fonte_recurso ?? ''}
                  onChange={(e) => definir('fonte_recurso', e.target.value)}
                  className={entrada}
                >
                  <option value="">-- Selecione --</option>
                  {(configuracao?.fontes_recurso ?? []).map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-slate-500">
                  Entra no título do documento (ex.: “{TIPO_COM_FONTE} FNS”).
                </p>
              </div>
            )}

            <div className="md:col-span-2">
              <label htmlFor="assunto" className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Assunto <span className="text-red-500">*</span>
              </label>
              <input
                id="assunto"
                name="assunto"
                type="text"
                value={campos.assunto ?? ''}
                onChange={(e) => definir('assunto', e.target.value)}
                className={entrada}
              />
            </div>
            <div>
              <label htmlFor="destinatario" className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Destinatário
              </label>
              <input
                id="destinatario"
                name="destinatario"
                type="text"
                value={campos.destinatario ?? ''}
                onChange={(e) => definir('destinatario', e.target.value)}
                className={entrada}
              />
            </div>
            <div>
              <label htmlFor="jurisdicao" className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Jurisdição
              </label>
              <input
                id="jurisdicao"
                name="jurisdicao"
                type="text"
                value={campos.jurisdicao ?? ''}
                onChange={(e) => definir('jurisdicao', e.target.value)}
                placeholder="Ex.: Municipal, Estadual"
                className={entrada}
              />
              <p className="mt-1 text-xs text-slate-500">
                Restringe a busca normativa à esfera informada.
              </p>
            </div>
            <div className="md:col-span-2">
              <label htmlFor="contexto" className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Contexto
              </label>
              <textarea
                id="contexto"
                name="contexto"
                rows={5}
                value={campos.contexto ?? ''}
                onChange={(e) => definir('contexto', e.target.value)}
                placeholder="Descreva a situação analisada, os fatos e o que precisa ser respondido…"
                className={entrada}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <SeletorCatalogo
              tipo="medicamento"
              titulo="Medicamentos (RENAME)"
              icone={faPills}
              escolhidos={medicamentos}
              aoEscolher={setMedicamentos}
            />
            <SeletorCatalogo
              tipo="procedimento"
              titulo="Procedimentos (Tabela SUS)"
              icone={faStethoscope}
              escolhidos={procedimentos}
              aoEscolher={setProcedimentos}
            />
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => melhorar.mutate()}
              disabled={melhorar.isPending || !(campos.contexto ?? '').trim()}
              className="inline-flex items-center gap-2 rounded-lg border border-teal-500 px-4 py-2 text-sm font-medium text-teal-700 transition-colors hover:bg-teal-50 disabled:opacity-50 dark:text-teal-300 dark:hover:bg-teal-900/20"
            >
              <FontAwesomeIcon
                icon={melhorar.isPending ? faSpinner : faWandMagicSparkles}
                spin={melhorar.isPending}
              />
              Melhorar contexto com IA
            </button>
            <button
              type="button"
              onClick={() => setVerContextos((v) => !v)}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300"
            >
              <FontAwesomeIcon icon={faFileMedical} />
              Reaproveitar contexto anterior
            </button>
          </div>

          {verContextos && (
            <ul className="max-h-60 space-y-2 overflow-y-auto rounded-xl border border-slate-200 p-3 dark:border-slate-700">
              {(contextos?.contextos ?? []).length === 0 ? (
                <li className="text-sm text-slate-500">
                  Nenhum parecer anterior com contexto escrito.
                </li>
              ) : (
                (contextos?.contextos ?? []).map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => {
                        definir('contexto', c.contexto)
                        setVerContextos(false)
                      }}
                      className="w-full rounded-lg p-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-700"
                    >
                      <span className="block font-medium text-slate-800 dark:text-slate-100">
                        {c.numero} — {c.assunto}
                      </span>
                      <span className="block truncate text-xs text-slate-500">
                        {c.contexto}
                      </span>
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}

          <div>
            <label
              htmlFor="documentos"
              className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300"
            >
              <FontAwesomeIcon icon={faPaperclip} className="mr-2 text-teal-600" />
              Documentos anexos
            </label>
            <input
              id="documentos"
              name="documentos"
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.txt"
              onChange={(e) => setAnexos(Array.from(e.target.files ?? []))}
              className="block w-full text-sm text-slate-600 dark:text-slate-400"
            />
            <p className="mt-1 text-xs text-slate-500">
              Ofício, laudo, decisão judicial: o texto dos anexos entra na
              análise.
            </p>
          </div>

          <button
            type="submit"
            disabled={criar.isPending}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-teal-600 to-cyan-600 px-6 py-3 font-semibold text-white shadow-lg transition-all hover:from-teal-700 hover:to-cyan-700 disabled:opacity-60"
          >
            <FontAwesomeIcon
              icon={criar.isPending ? faSpinner : faWandMagicSparkles}
              spin={criar.isPending}
            />
            Gerar Parecer
          </button>
        </form>
      </header>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <h2 className="mb-4 text-lg font-semibold text-slate-800 dark:text-slate-100">
          <FontAwesomeIcon icon={faFileMedical} className="mr-2 text-teal-600" />
          Pareceres emitidos
        </h2>
        {isLoading ? (
          <p className="text-sm text-slate-500">
            <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
            Carregando…
          </p>
        ) : pareceres.length === 0 ? (
          <p className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500 dark:border-slate-600">
            Nenhum parecer ainda.
          </p>
        ) : (
          <ul className="space-y-3">
            {pareceres.map((p) => {
              const pronto = (p.status || '').toUpperCase().startsWith('CONCLUID')
              const falhou = (p.status || '').toUpperCase() === 'ERRO'
              return (
                <li
                  key={p.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 p-4 dark:border-slate-700"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold text-slate-800 dark:text-slate-100">
                      {p.numero && <span className="mr-2 text-teal-600">{p.numero}</span>}
                      {p.assunto}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {p.tipo} · {p.criado_em} ·{' '}
                      <span
                        className={
                          falhou
                            ? 'text-red-600 dark:text-red-400'
                            : pronto
                              ? 'text-emerald-600 dark:text-emerald-400'
                              : 'text-amber-600 dark:text-amber-400'
                        }
                      >
                        <FontAwesomeIcon
                          icon={
                            falhou ? faExclamationTriangle : pronto ? faCheckCircle : faSpinner
                          }
                          spin={!pronto && !falhou}
                          className="mr-1"
                        />
                        {falhou ? 'Erro' : pronto ? 'Concluído' : `Em geração (${p.percentual}%)`}
                      </span>
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Link
                      to={pronto ? `/sus/${p.id}` : `/tasks/parecer/${p.id}`}
                      className="inline-flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200"
                    >
                      <FontAwesomeIcon icon={pronto ? faEye : faSpinner} />
                      {pronto ? 'Ver' : 'Acompanhar'}
                    </Link>
                    <button
                      type="button"
                      onClick={() => excluir.mutate(p.id)}
                      aria-label={`Excluir parecer ${p.numero}`}
                      className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-red-600 transition-colors hover:bg-red-50 dark:border-slate-600 dark:hover:bg-red-900/20"
                    >
                      <FontAwesomeIcon icon={faTrashAlt} />
                    </button>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </section>
    </div>
  )
}
